import { Link } from "react-router-dom";

interface ArticleData {
  slug: string;
  title: string;
  description: string;
  image: string;
  date: string;
  readTime: string;
  tags: string[];
  sections: { heading: string; paragraphs: string[] }[];
  faqs: { q: string; a: string }[];
}

interface ArticleLayoutProps {
  article: ArticleData;
}

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL;
  const pagePath = `/article/${article.slug}`;

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.description,
            author: { "@type": "Organization", name: "AllChinaBuy" },
            datePublished: article.date,
            dateModified: article.date,
            publisher: { "@type": "Organization", name: "AllChinaBuy", url: siteUrl },
            mainEntityOfPage: { "@type": "WebPage", "@id": `${siteUrl}${pagePath}` },
          }),
        }}
      />
      <section className="relative pt-24 pb-12 md:pt-28 md:pb-16 bg-background-100/70">
        <div className="px-4 md:px-6 max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors mb-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-price-tag-3-line"></i>
              {article.tags.join(", ")}
            </span>
          </div>
        </div>
      </section>

      <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover"
          loading="eager"
        />
      </section>

      <article className="relative py-10 md:py-14">
        <div className="px-4 md:px-6 max-w-4xl mx-auto">
          <div className="text-foreground-800 leading-relaxed space-y-10">
            <p className="text-base md:text-lg text-foreground-600 italic border-l-[3px] border-primary-300 pl-5 py-1">
              {article.description}
            </p>

            {article.sections.map((section, i) => (
              <section key={i}>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, j) => (
                  <p
                    key={j}
                    className="text-base leading-relaxed text-foreground-700"
                    style={j > 0 ? { marginTop: "1rem" } : undefined}
                    dangerouslySetInnerHTML={{ __html: p }}
                  />
                ))}
              </section>
            ))}

            {article.faqs.length > 0 && (
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-3">
                  {article.faqs.map((faq, i) => (
                    <details
                      key={i}
                      className="group border border-background-200/70 rounded-lg overflow-hidden"
                    >
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                        <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            <section className="bg-background-100/70 border border-background-200/70 rounded-lg p-6 text-center">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
                Ready to Start Sourcing Smarter?
              </h2>
              <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">
                Access the complete AllChinaBuySpreadsheet database on our main platform and browse all product categories.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center px-6 py-2.5 text-sm font-semibold rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Open Spreadsheet Hub
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
                >
                  <i className="ri-home-4-line"></i>
                  Return to Homepage
                </Link>
              </div>
            </section>
          </div>
        </div>
      </article>
    </div>
  );
}